/**
 * The canonical translators — ONE per SERVER event type, never one per consumer.
 *
 * Rule enforced here: a server event is parsed exactly once, into the RICHEST
 * shape any consumer needs. Consumers that want less select from that shape in
 * a thin wrapper next to their own view. Adding a consumer must never add a
 * parser.
 *
 * The `devops-event` stream is the reason this rule exists. It used to be
 * parsed TWICE: `views/devops/devopsLiveStatus.ts` flattened the envelope for
 * the pipeline/work-item grids and, in doing so, dropped `clientId` and the
 * headline-bearing summary fields; so `views/home/homeLive.ts` re-implemented
 * the listener to keep the raw envelope. Here the canonical
 * {@link DevOpsRealtimeEvent} carries BOTH — the untouched envelope, the ported
 * headline, and the flat grid projection — and each consumer picks a field.
 */
import type { DevOpsEvent, DevOpsEventEntity } from "views/devops/devops.types";
import {
  INSTANCE_ACTION_PROGRESS_EVENT,
  INSTANCE_STATUS_EVENT,
  INSTANCE_UPDATE_EVENT,
  type InstanceActionProgressEvent,
  type InstanceStatusEnvelope,
  type InstanceUpdateEnvelope,
} from "views/infrastructure/infrastructure.types";
import type { RealtimeChannel } from "./realtimeBus";

// ===========================================================================
// devops-event  (Azure DevOps webhook activity)
// ===========================================================================

/**
 * The Socket.io wire event name.
 *
 * The API emits DevOps activity via a Feathers CUSTOM SERVICE event:
 * `app.service('devops-pipelines').emit('devops-event', envelope)` (API
 * `devops-events.ts`, constants DEVOPS_EVENT_SERVICE / DEVOPS_EVENT). Feathers'
 * Socket.io transport delivers a custom service event under the name
 * `"<servicePath> <event>"` — exactly like the CRUD events. So the wire name is
 * `"devops-pipelines devops-event"`, NOT the bare `"devops-event"`. Listening on
 * the bare name silently receives nothing.
 */
export const DEVOPS_EVENT_WIRE = "devops-pipelines devops-event";

/**
 * The raw envelope the API publishes: `{ type, connectionId, clientId, summary,
 * at }`. Every field is optional — never trust a socket payload.
 */
export interface DevOpsEnvelope {
  /** e.g. 'build.complete', 'workitem.updated', 'git.pullrequest.created'. */
  type?: string;
  connectionId?: string;
  /** The IntraSync client the event maps to (null = internal-only). */
  clientId?: string | null;
  /** Small, secret-free summary of the underlying Azure payload. */
  summary?: Record<string, unknown>;
  /** When the event happened (ISO). */
  at?: string;
}

/** The canonical, single-parse shape for one `devops-event`. */
export interface DevOpsRealtimeEvent {
  /** The API envelope, untouched — carries `clientId` and the full summary. */
  envelope: DevOpsEnvelope;
  /** Headline, a faithful port of the API's `buildDevOpsEventTitle`. */
  title: string;
  /**
   * Flat projection for the PipelinesBoard / WorkItems grids, or `null` when
   * the envelope `type` maps to no grid entity. Consumers of the flat shape
   * skip null; consumers of the envelope do not.
   */
  flat: DevOpsEvent | null;
}

/** Map the API envelope `type` to the flat-event `entity` discriminator. */
function entityForType(type: string | undefined): DevOpsEventEntity | null {
  if (!type) return null;
  if (type.startsWith("build.")) return "build";
  if (type.startsWith("workitem.")) return "workItem";
  if (type.startsWith("git.pullrequest.")) return "pipeline";
  return null;
}

/**
 * Flatten the envelope into the shape the grids consume.
 *
 * The Azure project NAME under `summary.project` is intentionally NOT surfaced
 * as `projectId` (the stores scope on the Azure project GUID, which the
 * envelope does not carry) — connection-level scoping plus the row id match is
 * used instead, so events are not silently dropped by a name-vs-guid mismatch.
 */
function toFlatDevOpsEvent(envelope: DevOpsEnvelope): DevOpsEvent | null {
  const entity = entityForType(envelope.type);
  if (!entity) return null;
  const s = (envelope.summary ?? {}) as Record<string, unknown>;
  const base: DevOpsEvent = {
    entity,
    connectionId: envelope.connectionId,
    at: envelope.at,
  };
  if (entity === "workItem") {
    const wid = s.workItemId ?? s.id;
    return {
      ...base,
      workItemId:
        typeof wid === "string" || typeof wid === "number" ? wid : undefined,
      state: typeof s.state === "string" ? s.state : undefined,
      fields: {
        ...(typeof s.title === "string" ? { "System.Title": s.title } : {}),
        ...(typeof s.state === "string" ? { "System.State": s.state } : {}),
      },
    };
  }
  // build / pullrequest
  const def = s.definitionId;
  const bid = s.buildId;
  return {
    ...base,
    definitionId:
      typeof def === "string" || typeof def === "number" ? def : undefined,
    buildId:
      typeof bid === "string" || typeof bid === "number" ? bid : undefined,
    status:
      typeof s.status === "number" || typeof s.status === "string"
        ? s.status
        : undefined,
    result:
      typeof s.result === "number" || typeof s.result === "string"
        ? s.result
        : undefined,
    buildNumber: typeof s.buildNumber === "string" ? s.buildNumber : undefined,
    sourceBranch: typeof s.sourceBranch === "string" ? s.sourceBranch : undefined,
    finishTime: typeof s.finishTime === "string" ? s.finishTime : undefined,
  };
}

/**
 * Derive a small human-readable headline for a devops event — a faithful port
 * of the API's `buildDevOpsEventTitle` (devops/devops-events.ts), so a live row
 * shows the same text the persisted feed row will show after the next poll.
 */
export function buildDevOpsEventTitle(
  type: string,
  summary?: Record<string, unknown>
): string {
  const s: Record<string, unknown> = summary ?? {};
  const str = (v: unknown): string | undefined =>
    v === undefined || v === null ? undefined : String(v);

  let title: string;
  if (type === "build.complete") {
    const name = str(s.definitionName) ?? str(s.buildNumber) ?? str(s.buildId);
    const outcome = str(s.result) ?? str(s.status) ?? "completed";
    title = name ? `Build ${name} ${outcome}` : `Build ${outcome}`;
  } else if (type === "build.queued" || type === "build.cancelled") {
    const name = str(s.definitionName) ?? str(s.buildNumber) ?? str(s.buildId);
    const verb = type === "build.queued" ? "queued" : "cancelled";
    title = name ? `Build ${name} ${verb}` : `Build ${verb}`;
  } else if (type.startsWith("workitem.")) {
    const id = str(s.workItemId);
    const verb = type.endsWith(".created") ? "created" : "updated";
    const wiTitle = str(s.title);
    title = `Work item${id ? ` #${id}` : ""} ${verb}${wiTitle ? `: ${wiTitle}` : ""}`;
  } else if (type.startsWith("git.pullrequest.")) {
    const id = str(s.pullRequestId);
    const verb = type.split(".").pop() ?? "updated";
    const prTitle = str(s.title);
    title = `Pull request${id ? ` #${id}` : ""} ${verb}${prTitle ? `: ${prTitle}` : ""}`;
  } else if (
    type.startsWith("release.") ||
    type === "ms.vss-release.deployment-completed-event"
  ) {
    const name = str(s.releaseName) ?? str(s.releaseId);
    title = name ? `Release ${name}` : "Release event";
  } else {
    title = type || "DevOps event";
  }
  return title.slice(0, 400);
}

/** THE devops-event channel. Both DevOps grids and the dashboard read this. */
export const devopsChannel: RealtimeChannel<DevOpsRealtimeEvent> = {
  name: "devops-events",
  wireEvents: [DEVOPS_EVENT_WIRE],
  translate(_wireEvent, raw) {
    // Tolerate partial / unexpected payloads — never throw out of a socket cb.
    if (!raw || typeof raw !== "object") return null;
    const envelope = raw as DevOpsEnvelope;
    return {
      envelope,
      title: buildDevOpsEventTitle(envelope.type ?? "", envelope.summary),
      flat: toFlatDevOpsEvent(envelope),
    };
  },
};

// ===========================================================================
// Feathers service CRUD  (`"<servicePath> <created|patched|updated|removed>"`)
// ===========================================================================

export const CRUD_ACTIONS = [
  "created",
  "patched",
  "updated",
  "removed",
] as const;
export type CrudAction = (typeof CRUD_ACTIONS)[number];

/** One CRUD event off any Feathers service, service + action preserved. */
export interface ServiceCrudEvent<S extends string = string> {
  service: S;
  action: CrudAction;
  /** The emitted record — shape depends on the service; treated loosely. */
  record: Record<string, unknown>;
}

/**
 * Build a CRUD channel for a set of Feathers services. The wire names are
 * generated (`"<service> <action>"`) so a service list is the only input, and a
 * typo cannot produce a listener that quietly matches nothing at runtime
 * without also failing the `wireEvents` assertion in the bus tests.
 */
function crudChannel<S extends string>(
  name: string,
  services: readonly S[]
): RealtimeChannel<ServiceCrudEvent<S>> {
  const wireEvents: string[] = [];
  const lookup = new Map<string, { service: S; action: CrudAction }>();
  for (const service of services) {
    for (const action of CRUD_ACTIONS) {
      const wire = `${service} ${action}`;
      wireEvents.push(wire);
      lookup.set(wire, { service, action });
    }
  }
  return {
    name,
    wireEvents,
    translate(wireEvent, raw) {
      const hit = lookup.get(wireEvent);
      if (!hit) return null;
      // Tolerate partial / unexpected payloads — never throw out of a socket cb.
      const record = (raw && typeof raw === "object" ? raw : {}) as Record<
        string,
        unknown
      >;
      return { service: hit.service, action: hit.action, record };
    },
  };
}

/**
 * Services whose CRUD events the Clients area (list + Client 360) consumes.
 * Every one must be on the API's staff broadcast (`STAFF_BROADCAST_CRUD_PATHS`
 * in packages/api/src/channels.ts), or it is a subscription nothing is ever
 * published to — `apiPublishParity.test.ts` holds the two lists together.
 */
export const CLIENT_REALTIME_SERVICES = [
  "clients",
  "client-modules",
  "client-tools",
  "client-contacts",
  // Client 360's Whiteboard tab: the client's lanes and their week notes.
  "whiteboard-rows",
  "whiteboard-cells",
  // Client 360's Activity timeline (notes, logged comms) and Relationship tab.
  "communications",
  "relationship-items",
] as const;
export type ClientRealtimeService = (typeof CLIENT_REALTIME_SERVICES)[number];

/**
 * THE clients-area CRUD channel. Client 360 takes all four services; the
 * Clients LIST selects only `clients` rows out of the same stream rather than
 * opening a second listener set for the same events.
 */
export const clientCrudChannel = crudChannel(
  "clients-crud",
  CLIENT_REALTIME_SERVICES
);

/**
 * Services whose CRUD events the Modules area (list + Module 360) consumes.
 * `module-catalog` is the list itself; `module-owners` is the one a human
 * writes; `module-governance` / `module-releases` change only when the
 * Coordinator sync runs, which writes THROUGH the services, so a sync streams
 * into an open page like an edit would. All four are on the API's staff
 * broadcast (apiPublishParity.test.ts holds that).
 */
export const MODULE_REALTIME_SERVICES = [
  "module-catalog",
  "module-owners",
  "module-governance",
  "module-releases",
] as const;
export type ModuleRealtimeService = (typeof MODULE_REALTIME_SERVICES)[number];

/** THE modules-area CRUD channel (ModulesStore and ModuleDetailStore). */
export const moduleCrudChannel = crudChannel(
  "modules-crud",
  MODULE_REALTIME_SERVICES
);

/**
 * THE tickets CRUD channel. Not a staff broadcast: the API sends a ticket row
 * only to the accounts its read admits (requester, assignee, the team,
 * operators — packages/api/src/channels.ts `ticketReaderChannels`), so
 * everything that arrives here is something this person may read.
 */
export const TICKET_REALTIME_SERVICES = ["tickets"] as const;
export const ticketCrudChannel = crudChannel(
  "tickets-crud",
  TICKET_REALTIME_SERVICES
);

/** Services whose CRUD events the notification bell's fallback path watches. */
export const NOTIFICATION_REALTIME_SERVICES = ["notifications"] as const;
export type NotificationRealtimeService =
  (typeof NOTIFICATION_REALTIME_SERVICES)[number];

/** THE notifications CRUD channel (backs `stores/RealtimeStore`). */
export const notificationCrudChannel = crudChannel(
  "notifications-crud",
  NOTIFICATION_REALTIME_SERVICES
);

// ===========================================================================
// Meeting Intelligence  (read-ai custom service events)
// ===========================================================================

export interface MeetingIntelEvent {
  /** Logical event kind. */
  kind:
    | "transcript-received"
    | "meeting-processed"
    | "meeting-enriched"
    | "meeting-distributed"
    | "meeting-analyzed"
    | "meeting-marked-internal"
    | "meeting-unmarked-internal"
    | "meeting-mapped"
    | "memory-consolidated"
    | "assignment-accepted"
    | "assignment-rejected"
    | "assignment-completed"
    | "assignment-module-changed"
    | "action-item-changed"
    | "action-items-closed";
  payload: Record<string, unknown>;
}

/**
 * Wire message name -> logical kind.
 *
 * `meeting-marked-internal` is its own kind and not a second name for
 * `meeting-processed`, because the API deliberately kept them apart: the API's
 * `MEETING_MARKED_INTERNAL` (read-ai/parties/meeting-mappings.ts, registered in
 * that service's `events`) says a human resolved a meeting as IntraSync's own
 * and the CLIENT-INDEPENDENT half of the pipeline ran, while
 * `meeting-processed` is the client pipeline's "extraction committed" signal
 * that `action-distribution` acts on. Two surfaces move on the internal one —
 * the Mapping Queue loses the row and Internal Meetings gains it — and neither
 * had any way to hear about it before (C7).
 *
 * `meeting-analyzed` is the same kind of separation, for the producer path
 * (2026-09-15). HQ no longer runs its own analysis pass; `zach-gantry` pushes
 * the result to `POST /meeting-analysis-result`, and the API's
 * `MEETING_ANALYZED_EVENT` fires once that push is STORED (never for a replay).
 * It is not `meeting-processed` because `action-distribution` distributes on
 * that one, and the result service has already distributed — re-using the name
 * would run the gate twice over the same items.
 */
const MEETING_INTEL_EVENT_MAP: Record<string, MeetingIntelEvent["kind"]> = {
  "read-ai-webhook transcript-received": "transcript-received",
  "client-intelligence meeting-processed": "meeting-processed",
  // HQ's own pass FIRES `meeting-processed` before its enrichment (relationship
  // signals, analyses) is written; this one says the enrichment is done (#3820).
  "client-intelligence meeting-enriched": "meeting-enriched",
  // A distribution run routed a meeting's items — `{ meetingId, clientId,
  // distributed }`: new work exists, on every path that distributes (#3820).
  "action-distribution meeting-distributed": "meeting-distributed",
  "meeting-analysis-result meeting-analyzed": "meeting-analyzed",
  "meeting-mappings meeting-marked-internal": "meeting-marked-internal",
  // The reverse transition: Internal Meetings loses the row, the Mapping
  // Queue gains it back.
  "meeting-mappings meeting-unmarked-internal": "meeting-unmarked-internal",
  // A meeting mapped to a client (`assign`, 2026-11-02): `{ meetingId,
  // clientId, previousClientId, migratedAssignments }`. The row leaves the
  // Mapping Queue, and the meeting — with any work that moved with it — joins
  // its client's Client 360. Under Gantry authority nothing else says so.
  "meeting-mappings meeting-mapped": "meeting-mapped",
  "client-intelligence memory-consolidated": "memory-consolidated",
  // `assignment-approved` was removed from the API's `events` list on
  // 2026-09-18 along with approval itself; `assignment-accepted` is what fires
  // now (the developer took the work, which is when the ADO work item is
  // filed) and `assignment-completed` when their PR closed it. A wire name the
  // API no longer emits is a subscription that is silently inert.
  "read-ai-assignments assignment-accepted": "assignment-accepted",
  "read-ai-assignments assignment-rejected": "assignment-rejected",
  "read-ai-assignments assignment-completed": "assignment-completed",
  // An assignment moved to another module (set-module, #3820; a retarget of
  // accepted work or its undo, 2026-11-02): `{ assignmentId, moduleId,
  // previousModuleId }`, plus `clientId` / `previousClientId` when a retarget
  // also took it to another client. Work is counted per module (Client 360's
  // Deliverables "Tasks" chip, Work Status) and a review item is decided by
  // its module's maintainers, so all follow it.
  "read-ai-assignments assignment-module-changed": "assignment-module-changed",
  // A close or reopen of one item: the payload is the item row (with its
  // `meetingId`), as a staff GET returns it.
  "read-ai-action-items patched": "action-item-changed",
  // A bulk close (`close-bulk`, carrying the `meetingId`) or a stale sweep
  // (`close-stale`, across meetings): the payload is the action's ENVELOPE,
  // never a row — which is why the `read-ai-action-items` ServiceStore does
  // not listen to `created` (serviceStores/meetingIntelligence.stores.ts).
  "read-ai-action-items created": "action-items-closed",
};

/** THE Meeting Intelligence channel. */
export const meetingIntelChannel: RealtimeChannel<MeetingIntelEvent> = {
  name: "meeting-intel",
  wireEvents: Object.keys(MEETING_INTEL_EVENT_MAP),
  translate(wireEvent, raw) {
    const kind = MEETING_INTEL_EVENT_MAP[wireEvent];
    if (!kind) return null;
    const payload = (raw && typeof raw === "object" ? raw : {}) as Record<
      string,
      unknown
    >;
    return { kind, payload };
  },
};

// ===========================================================================
// Meeting review  ("this review changed" — id-only, to its readers alone)
// ===========================================================================

/**
 * The Socket.io wire name of the review signal: the API's `meeting-reviews`
 * custom event `review-changed` (packages/api review-signal.ts).
 */
export const MEETING_REVIEW_CHANGED_WIRE = "meeting-reviews review-changed";

/** All the signal says: WHICH review changed. Never how. */
export interface MeetingReviewChangedEvent {
  meetingId: number;
}

/**
 * THE meeting-review channel (#3820). NOT a staff broadcast: a review is built
 * for its reader, so the API sends `{ meetingId }` and nothing else, and only
 * to the people who may open that review (its audience and operators; for a
 * private meeting its members and administrators). The page answers it by
 * re-reading its OWN view — nothing about the review rides the event. An
 * envelope without a usable meeting id is dropped.
 */
export const meetingReviewChannel: RealtimeChannel<MeetingReviewChangedEvent> = {
  name: "meeting-review",
  wireEvents: [MEETING_REVIEW_CHANGED_WIRE],
  translate(_wireEvent, raw) {
    // Tolerate partial / unexpected payloads — never throw out of a socket cb.
    const payload = (raw && typeof raw === "object" ? raw : {}) as { meetingId?: unknown };
    const meetingId = Number(payload.meetingId);
    return Number.isInteger(meetingId) && meetingId > 0 ? { meetingId } : null;
  },
};

// ===========================================================================
// Infrastructure fleet status  (periodic reconcile sweep fan-out)
// ===========================================================================

/**
 * THE fleet-status channel: `"client-instances instance-status"`, a Feathers
 * custom service event the API's reconcile sweep publishes to the staff
 * channel. An envelope without an `instances` array is dropped (the grid would
 * have nothing to apply), which the bus counts as a drop rather than a silence.
 *
 * The raw bidirectional streams for one container (`instance:logs:*`,
 * `instance:stats:*`, `instance:exec:*`) are NOT channels — they are
 * request/response conversations, so `views/infrastructure/instanceStreams.ts`
 * leases the shared socket from the bus and drives them directly.
 */
export const instanceStatusChannel: RealtimeChannel<InstanceStatusEnvelope> = {
  name: "instance-status",
  wireEvents: [INSTANCE_STATUS_EVENT],
  translate(_wireEvent, raw) {
    const envelope = (raw && typeof raw === "object"
      ? raw
      : {}) as InstanceStatusEnvelope;
    if (!Array.isArray(envelope.instances)) return null;
    return envelope;
  },
};

/**
 * Update-availability fan-out: `"client-instances instance-update-available"`,
 * published by the registry sweep ONLY on an instance's false→true transition
 * (never on true→false — the next status/reload clears the badge). The
 * envelope names the instances and their latest image refs; a malformed one
 * is dropped.
 */
export const instanceUpdateChannel: RealtimeChannel<InstanceUpdateEnvelope> = {
  name: "instance-update",
  wireEvents: [INSTANCE_UPDATE_EVENT],
  translate(_wireEvent, raw) {
    const envelope = (raw && typeof raw === "object"
      ? raw
      : {}) as InstanceUpdateEnvelope;
    if (!Array.isArray(envelope.instances)) return null;
    return envelope;
  },
};

/**
 * Every channel the app registers. The freshness-policy test asserts that each
 * `live` surface names one of these, so the policy document cannot drift away
 * from the code.
 */
/**
 * Per-step progress of one container action:
 * `"client-instances instance-action-progress"`, emitted by the API's
 * instance-actions service while a deploy / rollback / move / restart runs,
 * for the dialog that sent it. An event without an instance id, a run id and
 * a phase is dropped — the dialog keys on all three.
 */
export const instanceActionProgressChannel: RealtimeChannel<InstanceActionProgressEvent> = {
  name: "instance-action-progress",
  wireEvents: [INSTANCE_ACTION_PROGRESS_EVENT],
  translate(_wireEvent, raw) {
    const event = (raw && typeof raw === "object"
      ? raw
      : {}) as Partial<InstanceActionProgressEvent>;
    if (
      typeof event.instanceId !== "string" ||
      typeof event.runId !== "string" ||
      typeof event.phase !== "string"
    ) {
      return null;
    }
    return event as InstanceActionProgressEvent;
  },
};

export const ALL_CHANNELS: readonly RealtimeChannel<unknown>[] = [
  devopsChannel,
  clientCrudChannel,
  moduleCrudChannel,
  ticketCrudChannel,
  notificationCrudChannel,
  meetingIntelChannel,
  meetingReviewChannel,
  instanceStatusChannel,
  instanceUpdateChannel,
  instanceActionProgressChannel,
] as RealtimeChannel<unknown>[];
